import React, { useState } from "react";
import { X, Loader2, Download } from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function TraktImportModal({ open, onClose, onDone }) {
  const [username, setUsername] = useState("");
  const [listSlug, setListSlug] = useState("watchlist");
  const [target, setTarget] = useState("watchlist");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/me/import/trakt", {
        username: username.trim(), list_slug: listSlug.trim() || "watchlist", target,
      });
      toast.success(`Imported ${data.imported} items${data.failed ? ` (${data.failed} failed)` : ""}`);
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-6 w-full max-w-md relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/10">
          <X className="w-4 h-4" />
        </button>
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Download className="w-5 h-5 text-[var(--tt-primary)]" />
          Import from Trakt.tv
        </h2>
        <p className="text-xs text-[var(--tt-text-muted)] mt-2">
          Import a public Trakt list. List must be public. Custom lists use the slug from the list URL.
        </p>
        <form onSubmit={submit} className="space-y-3 mt-5">
          <label className="block">
            <div className="text-xs text-[var(--tt-text-muted)] mb-1">Trakt username</div>
            <input required value={username} onChange={(e) => setUsername(e.target.value)}
              data-testid="trakt-username-input"
              placeholder="e.g. justin"
              className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]" />
          </label>
          <label className="block">
            <div className="text-xs text-[var(--tt-text-muted)] mb-1">List slug</div>
            <input value={listSlug} onChange={(e) => setListSlug(e.target.value)}
              data-testid="trakt-slug-input"
              placeholder="watchlist or favorites or custom-list-slug"
              className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]" />
          </label>
          <label className="block">
            <div className="text-xs text-[var(--tt-text-muted)] mb-1">Import into</div>
            <select value={target} onChange={(e) => setTarget(e.target.value)}
              data-testid="trakt-target-select"
              className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]">
              <option value="watchlist">My Watchlist</option>
              <option value="favorites">My Favorites</option>
            </select>
          </label>
          <button type="submit" disabled={busy}
            data-testid="trakt-submit-btn"
            className="w-full bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Import
          </button>
        </form>
      </div>
    </div>
  );
}
